import { formatTournamentDate } from '../utils/tournamentTime';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

import Loading from '../components/Loading';
const FILTERS = ['all', 'scheduled', 'live', 'completed'];

function statusLabel(status) {
  return String(status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function statusColor(status) {
  return ({ scheduled: '#f59e0b', live: '#10b981', completed: '#3b82f6', closed: '#6b7280', cancelled: '#ef4444' })[status] || '#aaa';
}

export default function MyTournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.tournaments.getMyTournaments();
        const rows = res.data?.tournaments || res.data?.data || [];
        if (active) setTournaments(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (active) setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load your tournaments');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const visible = useMemo(() => {
    if (filter === 'all') return tournaments;
    return tournaments.filter(t => t.status === filter);
  }, [tournaments, filter]);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '18px' }}>
        <Link to="/tournaments" style={{ color: '#888', textDecoration: 'none', fontSize: '14px' }}>
          ← Back to tournaments
        </Link>
        <h1 style={{ margin: '10px 0 4px' }}>🏆 My Tournaments</h1>
        <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
          Tournaments you've joined, including your completed results.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '10px', margin: '20px 0', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid #333',
              background: filter === f ? '#00d4aa' : 'transparent',
              color: filter === f ? '#000' : '#aaa',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: filter === f ? 'bold' : 'normal',
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {error ? (
        <div style={{ background: '#3b1111', color: '#ef4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      ) : loading ? (
        <Loading variant="section" label="" />
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          <p style={{ fontSize: '48px', margin: 0 }}>🏆</p>
          <p>{filter === 'all' ? 'You have not joined any tournaments yet.' : `No ${filter} tournaments in your history.`}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visible.map(t => {
            const maxPlays = Number(t.max_plays_per_player || 0);
            const playsUsed = Number(t.plays_used || 0);
            const remaining = t.playsRemaining !== undefined ? Number(t.playsRemaining) : Math.max(0, maxPlays - playsUsed);
            const entryFee = Number(t.entry_fee || 0);
            return (
              <div key={t.id} style={{ background: '#1a1a2e', borderRadius: '12px', padding: '20px', border: '1px solid #2a2a3e' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>{t.game_type === 'whot' ? '🃏' : '🎮'}</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>{t.name}</h3>
                        <span style={{ color: '#888', fontSize: '13px' }}>{t.game_type === 'whot' ? 'Whot' : 'Archived tournament'}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
                      <span>💰 Entry: <strong>₦{entryFee.toLocaleString()}</strong></span>
                      <span>🎮 Plays: <strong>{playsUsed}/{maxPlays}</strong></span>
                      <span style={{ color: statusColor(t.status) }}>● {statusLabel(t.status)}</span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                      {formatTournamentDate(t.start_time)} — {formatTournamentDate(t.end_time)}
                    </div>
                    {t.status === 'live' && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#f59e0b', fontWeight: 700 }}>
                        {remaining === 0 ? 'Play limit reached' : `${remaining} play${remaining === 1 ? '' : 's'} remaining`}
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/tournaments/${t.id}`}
                    style={{ padding: '8px 16px', background: '#2a2a3e', color: '#aaa', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', textAlign: 'center', whiteSpace: 'nowrap' }}
                  >
                    View Details
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
